import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import LanguageSelector from '../LanguageSelector';
import NotificationBell from '../NotificationBell';
import axios from 'axios';


import { API_URL } from '../../utils/apiUrl';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadFeedbackCount, setUnreadFeedbackCount] = useState(0);
  const [risorseMenuOpen, setRisorseMenuOpen] = useState(false);
  const [loginMenuOpen, setLoginMenuOpen] = useState(false);
  const [utileMenuOpen, setUtileMenuOpen] = useState(false);
  const [noiMenuOpen, setNoiMenuOpen] = useState(false);
  const risorseMenuRef = React.useRef(null);
  const loginMenuRef = React.useRef(null);
  const utileMenuRef = React.useRef(null);
  const noiMenuRef = React.useRef(null);
  const navigate = useNavigate();
  const { user, isAuthenticated, isPrivato, isAgenzia, isAdmin, isBlogger, isFiorista, isProfessionista, logout } = useAuth();
  const { t } = useLanguage();

  // Close menus on outside click
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (risorseMenuRef.current && !risorseMenuRef.current.contains(event.target)) {
        setRisorseMenuOpen(false);
      }
      if (loginMenuRef.current && !loginMenuRef.current.contains(event.target)) {
        setLoginMenuOpen(false);
      }
      if (utileMenuRef.current && !utileMenuRef.current.contains(event.target)) {
        setUtileMenuOpen(false);
      }
      if (noiMenuRef.current && !noiMenuRef.current.contains(event.target)) {
        setNoiMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
   
  }, []);

  // Fetch unread feedback count for agencies
  useEffect(() => {
    if (isAgenzia) {
      fetchUnreadCount();
      // Poll every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
   
  }, [isAgenzia]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ricordini/feedback/all`, {
        params: { solo_non_letti: true, limit: 1 }
      });
      setUnreadFeedbackCount(response.data.unread_count || 0);
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  };

  const handleAreaRiservata = () => {
    if (isAuthenticated) {
      if (isAdmin) {
        navigate('/admin/dashboard');
      } else if (isAgenzia) {
        navigate('/dashboard-agenzia');
      } else if (isBlogger) {
        navigate('/blogger/dashboard');
      } else if (isFiorista) {
        navigate('/fiorista/dashboard');
      } else if (isProfessionista) {
        navigate('/professionista/dashboard');
      } else if (isPrivato) {
        navigate('/area-utente');
      } else {
        navigate('/area-utente');
      }
    } else {
      navigate('/login');
    }
  };

  const handleLogout = () => {
    if (window.confirm('Sei sicuro di voler uscire?')) {
      logout();
      navigate('/');
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-background-light/95 backdrop-blur-sm border-b border-terra-siena/10 px-4 lg:px-12 py-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center h-20">
          {/* Logo - spostato più a sinistra */}
          <Link to="/" className="flex items-center gap-2 -ml-2">
            <img loading="lazy" decoding="async" 
              src="https://static.prod-images.emergentagent.com/jobs/a5f866e2-e980-48dc-baab-bd61582b9a1c/images/664b0963ea78b9ebdb75db1f8ce1af51435c4d4bb40b926db45f6a662822a9d6.png" 
              alt="Resta in Pace" 
              className="h-8 w-8 object-contain"
            />
            <span className="text-2xl tracking-tight text-[#8B7355]" style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif", fontStyle: 'italic', fontWeight: 500 }}>
              Resta in Pace
            </span>
          </Link>

          {/* Main Nav - Desktop */}
          <nav className="hidden md:flex items-center gap-5">
            <Link to="/necrologi" className="text-sm font-semibold hover:text-primary transition-colors">
              Cerca
            </Link>
            <Link to="/agenzie" className="text-sm font-semibold hover:text-primary transition-colors">
              Onoranze Funebri
            </Link>
            <Link to="/supporto-al-lutto" className="text-sm font-semibold hover:text-primary transition-colors" data-testid="supporto-lutto-nav">
              Supporto al Lutto
            </Link>
            
            {/* Utile Dropdown - Blog, Broadcast, FAQ, Cimiteri */}
            <div className="relative" ref={utileMenuRef}>
              <button
                onClick={() => setUtileMenuOpen(!utileMenuOpen)}
                className="flex items-center gap-1 text-sm font-semibold hover:text-primary transition-colors"
                data-testid="utile-dropdown-btn"
              >
                Utile
                <span className={`material-symbols-outlined text-sm transition-transform ${utileMenuOpen ? 'rotate-180' : ''}`}>
                  expand_more
                </span>
              </button>
              
              {utileMenuOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-3 z-50">
                  <Link 
                    to="/cimiteri" 
                    className="flex items-center gap-3 px-4 py-3 hover:bg-soft-cream transition-colors"
                    onClick={() => setUtileMenuOpen(false)}
                    data-testid="utile-cimiteri-link"
                  >
                    <span className="material-symbols-outlined text-primary">grass</span>
                    <div>
                      <p className="text-sm font-semibold text-charcoal">Servizi Cimiteriali</p>
                      <p className="text-xs text-muted-gold">Cimiteri e luoghi di culto</p>
                    </div>
                  </Link>

                  <Link 
                    to="/blog" 
                    className="flex items-center gap-3 px-4 py-3 hover:bg-soft-cream transition-colors"
                    onClick={() => setUtileMenuOpen(false)}
                  >
                    <span className="material-symbols-outlined text-primary">article</span>
                    <div>
                      <p className="text-sm font-semibold text-charcoal">Blog</p>
                      <p className="text-xs text-muted-gold">Articoli e approfondimenti</p>
                    </div>
                  </Link>
                  
                  <Link 
                    to="/podcast" 
                    className="flex items-center gap-3 px-4 py-3 hover:bg-soft-cream transition-colors"
                    onClick={() => setUtileMenuOpen(false)}
                  >
                    <span className="material-symbols-outlined text-primary">podcasts</span>
                    <div>
                      <p className="text-sm font-semibold text-charcoal">Broadcast</p>
                      <p className="text-xs text-muted-gold">Podcast e contenuti audio</p>
                    </div>
                  </Link>
                  
                  <Link 
                    to="/faq" 
                    className="flex items-center gap-3 px-4 py-3 hover:bg-soft-cream transition-colors"
                    onClick={() => setUtileMenuOpen(false)}
                  >
                    <span className="material-symbols-outlined text-primary">help</span>
                    <div>
                      <p className="text-sm font-semibold text-charcoal">FAQ</p>
                      <p className="text-xs text-muted-gold">Domande frequenti</p>
                    </div>
                  </Link>
                </div>
              )}
            </div>
            
            {/* Risorse/Guide Dropdown */}
            <div className="relative" ref={risorseMenuRef}>
              <button
                onClick={() => setRisorseMenuOpen(!risorseMenuOpen)}
                className="flex items-center gap-1 text-sm font-semibold hover:text-primary transition-colors"
                data-testid="risorse-dropdown-btn"
              >
                Risorse
                <span className={`material-symbols-outlined text-sm transition-transform ${risorseMenuOpen ? 'rotate-180' : ''}`}>
                  expand_more
                </span>
              </button>
              
              {risorseMenuOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-3 z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <span className="text-xs font-bold text-muted-gold uppercase tracking-wider">Servizi</span>
                  </div>
                  
                  <Link 
                    to="/professionisti" 
                    className="flex items-center gap-3 px-4 py-3 hover:bg-soft-cream transition-colors"
                    onClick={() => setRisorseMenuOpen(false)}
                  >
                    <span className="material-symbols-outlined text-primary">workspace_premium</span>
                    <div>
                      <p className="text-sm font-semibold text-charcoal">Cerca Professionista</p>
                      <p className="text-xs text-muted-gold">Psicologi, avvocati, marmisti...</p>
                    </div>
                  </Link>
                  
                  <Link 
                    to="/fioristi" 
                    className="flex items-center gap-3 px-4 py-3 hover:bg-soft-cream transition-colors"
                    onClick={() => setRisorseMenuOpen(false)}
                  >
                    <span className="material-symbols-outlined text-primary">spa</span>
                    <div>
                      <p className="text-sm font-semibold text-charcoal">Fiori e Pensieri</p>
                      <p className="text-xs text-muted-gold">Fioristi della tua zona</p>
                    </div>
                  </Link>
                  
                  <Link 
                    to="/messe-suffraggio" 
                    className="flex items-center gap-3 px-4 py-3 hover:bg-soft-cream transition-colors"
                    onClick={() => setRisorseMenuOpen(false)}
                  >
                    <span className="material-symbols-outlined text-primary">church</span>
                    <div>
                      <p className="text-sm font-semibold text-charcoal">Messe in Suffragio</p>
                      <p className="text-xs text-muted-gold">Celebrazioni e commemorazioni</p>
                    </div>
                  </Link>
                  
                  <div className="border-t border-gray-100 mt-2 pt-2">
                    <div className="px-4 py-2">
                      <span className="text-xs font-bold text-muted-gold uppercase tracking-wider">Guide</span>
                    </div>
                    
                    <Link 
                      to="/guide" 
                      className="flex items-center gap-3 px-4 py-3 hover:bg-soft-cream transition-colors"
                      onClick={() => setRisorseMenuOpen(false)}
                    >
                      <span className="material-symbols-outlined text-primary">auto_stories</span>
                      <div>
                        <p className="text-sm font-semibold text-charcoal">Guide Pratiche</p>
                        <p className="text-xs text-muted-gold">Supporto al lutto e pratiche</p>
                      </div>
                    </Link>
                    
                    <Link 
                      to="/documenti-legali" 
                      className="flex items-center gap-3 px-4 py-3 hover:bg-soft-cream transition-colors"
                      onClick={() => setRisorseMenuOpen(false)}
                    >
                      <span className="material-symbols-outlined text-primary">contract</span>
                      <div>
                        <p className="text-sm font-semibold text-charcoal">Documenti Legali</p>
                        <p className="text-xs text-muted-gold">Moduli e contratti</p>
                      </div>
                    </Link>
                    
                    <Link 
                      to="/biblioteca-documenti" 
                      className="flex items-center gap-3 px-4 py-3 hover:bg-soft-cream transition-colors"
                      onClick={() => setRisorseMenuOpen(false)}
                    >
                      <span className="material-symbols-outlined text-primary">library_books</span>
                      <div>
                        <p className="text-sm font-semibold text-charcoal">Biblioteca PDF Gratuite</p>
                        <p className="text-xs text-muted-gold">18 guide scaricabili</p>
                      </div>
                    </Link>
                  </div>
                </div>
              )}
            </div>
            
            {/* Noi Dropdown - Chi Siamo, Contatti, Segnalazione */}
            <div className="relative" ref={noiMenuRef}>
              <button
                onClick={() => setNoiMenuOpen(!noiMenuOpen)}
                className="flex items-center gap-1 text-sm font-semibold hover:text-primary transition-colors"
                data-testid="noi-dropdown-btn"
              >
                Noi
                <span className={`material-symbols-outlined text-sm transition-transform ${noiMenuOpen ? 'rotate-180' : ''}`}>
                  expand_more
                </span>
              </button>
              
              {noiMenuOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-3 z-50">
                  <Link 
                    to="/chi-siamo" 
                    className="flex items-center gap-3 px-4 py-3 hover:bg-soft-cream transition-colors"
                    onClick={() => setNoiMenuOpen(false)}
                  >
                    <span className="material-symbols-outlined text-primary">groups</span>
                    <div>
                      <p className="text-sm font-semibold text-charcoal">Chi Siamo</p>
                      <p className="text-xs text-muted-gold">La nostra storia e missione</p>
                    </div>
                  </Link>
                  
                  <Link 
                    to="/contatti" 
                    className="flex items-center gap-3 px-4 py-3 hover:bg-soft-cream transition-colors"
                    onClick={() => setNoiMenuOpen(false)}
                  >
                    <span className="material-symbols-outlined text-primary">mail</span>
                    <div>
                      <p className="text-sm font-semibold text-charcoal">Contatti / Segnalazioni</p>
                      <p className="text-xs text-muted-gold">Scrivici o segnala un problema</p>
                    </div>
                  </Link>
                </div>
              )}
            </div>
            
            {/* Donazioni Button */}
            <Link 
              to="/associazioni" 
              className="flex items-center gap-1.5 px-4 py-2 bg-primary/10 text-primary font-semibold rounded-lg hover:bg-primary hover:text-white transition-all text-sm"
              data-testid="donazioni-btn"
            >
              <span className="material-symbols-outlined text-sm">favorite</span>
              Donazioni
            </Link>
          </nav>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            {/* Language Selector */}
            <LanguageSelector />
            
            {isAuthenticated ? (
              <>
                <div className="hidden lg:flex items-center gap-3">
                  <span className="text-sm text-muted-gold">
                    {t('header.welcome')}, <span className="font-semibold text-charcoal">{user?.nome}</span>
                  </span>
                  
                  {/* Dashboard Button with Notification Badge */}
                  <button
                    onClick={handleAreaRiservata}
                    className="relative flex items-center gap-2 px-4 py-2 text-sm font-bold border-2 border-primary text-charcoal rounded-lg hover:bg-primary transition-all"
                    data-testid="area-riservata-btn"
                  >
                    <span className="material-symbols-outlined text-sm">
                      {isAdmin ? 'admin_panel_settings' : isAgenzia ? 'business' : isBlogger ? 'edit_note' : isFiorista ? 'local_florist' : isProfessionista ? 'work' : 'person'}
                    </span>
                    {isAdmin ? 'Pannello Admin' : (isAgenzia || isFiorista || isProfessionista || isBlogger) ? t('header.dashboard') : t('header.reservedArea')}
                    
                    {/* Notification Badge for Agencies */}
                    {isAgenzia && unreadFeedbackCount > 0 && (
                      <span 
                        className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1.5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full animate-pulse"
                        data-testid="notification-badge"
                      >
                        {unreadFeedbackCount > 99 ? '99+' : unreadFeedbackCount}
                      </span>
                    )}
                  </button>
                  
                  <NotificationBell />
                  
                  <button
                    onClick={handleLogout}
                    className="p-2 text-muted-gold hover:text-deep-burgundy transition-colors"
                    title={t('common.logout')}
                  >
                    <span className="material-symbols-outlined">logout</span>
                  </button>
                </div>
              </>
            ) : (
              <div className="relative hidden lg:block" ref={loginMenuRef}>
                <button
                  onClick={() => setLoginMenuOpen(!loginMenuOpen)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-primary text-charcoal rounded-md hover:bg-primary transition-all"
                  data-testid="login-btn"
                >
                  <span className="material-symbols-outlined text-sm">login</span>
                  Log in
                  <span className="material-symbols-outlined text-sm">{loginMenuOpen ? 'expand_less' : 'expand_more'}</span>
                </button>
                
                {/* Login Dropdown Menu */}
                {loginMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-lg border border-soft-cream z-50 overflow-hidden">
                    <div className="px-4 py-3 bg-soft-cream/50 border-b border-soft-cream">
                      <p className="text-xs font-bold text-muted-gold uppercase tracking-wider">Accedi come</p>
                    </div>
                    
                    <Link 
                      to="/login" 
                      className="flex items-center gap-3 px-4 py-3 hover:bg-soft-cream transition-colors"
                      onClick={() => setLoginMenuOpen(false)}
                    >
                      <span className="material-symbols-outlined text-primary">person</span>
                      <div>
                        <p className="text-sm font-semibold text-charcoal">Utente Privato</p>
                        <p className="text-xs text-muted-gold">Area personale necrologi</p>
                      </div>
                    </Link>
                    
                    <Link 
                      to="/login-agenzia" 
                      className="flex items-center gap-3 px-4 py-3 hover:bg-soft-cream transition-colors"
                      onClick={() => setLoginMenuOpen(false)}
                    >
                      <span className="material-symbols-outlined text-primary">church</span>
                      <div>
                        <p className="text-sm font-semibold text-charcoal">Onoranze Funebri</p>
                        <p className="text-xs text-muted-gold">Dashboard gestione</p>
                      </div>
                    </Link>
                    
                    <div className="border-t border-soft-cream">
                      <Link 
                        to="/fiorista/login" 
                        className="flex items-center gap-3 px-4 py-3 hover:bg-soft-cream transition-colors"
                        onClick={() => setLoginMenuOpen(false)}
                      >
                        <span className="material-symbols-outlined text-primary">local_florist</span>
                        <div>
                          <p className="text-sm font-semibold text-charcoal">Negozio Floreale</p>
                          <p className="text-xs text-muted-gold">Gestisci i tuoi prodotti</p>
                        </div>
                      </Link>
                      
                      <Link 
                        to="/professionista/login" 
                        className="flex items-center gap-3 px-4 py-3 hover:bg-soft-cream transition-colors"
                        onClick={() => setLoginMenuOpen(false)}
                      >
                        <span className="material-symbols-outlined text-primary">handyman</span>
                        <div>
                          <p className="text-sm font-semibold text-charcoal">Libero Professionista</p>
                          <p className="text-xs text-muted-gold">Gestisci il tuo profilo</p>
                        </div>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-muted-gold lg:hidden"
              data-testid="mobile-menu-btn"
            >
              <span className="material-symbols-outlined">{mobileMenuOpen ? 'close' : 'menu'}</span>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-soft-cream" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4">
              <Link
                to="/necrologi"
                className="text-sm font-semibold hover:text-primary transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('header.searchObituaries')}
              </Link>
              <Link
                to="/agenzie"
                className="text-sm font-semibold hover:text-primary transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Onoranze Funebri
              </Link>
              <Link
                to="/supporto-al-lutto"
                className="text-sm font-semibold hover:text-primary transition-colors"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-supporto-lutto-link"
              >
                Supporto al Lutto
              </Link>
              
              {/* Utile Section - Mobile */}
              <div className="border-t border-soft-cream pt-3 mt-1">
                <p className="text-xs font-bold text-muted-gold uppercase tracking-wider mb-2">Utile</p>
                <div className="flex flex-col gap-2 pl-2">
                  <Link
                    to="/cimiteri"
                    className="text-sm font-semibold hover:text-primary transition-colors flex items-center gap-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="material-symbols-outlined text-sm text-primary">grass</span>
                    Servizi Cimiteriali
                  </Link>
                  <Link
                    to="/blog"
                    className="text-sm font-semibold hover:text-primary transition-colors flex items-center gap-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="material-symbols-outlined text-sm text-primary">article</span>
                    Blog
                  </Link>
                  <Link
                    to="/podcast"
                    className="text-sm font-semibold hover:text-primary transition-colors flex items-center gap-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="material-symbols-outlined text-sm text-primary">podcasts</span>
                    Broadcast
                  </Link>
                  <Link
                    to="/faq"
                    className="text-sm font-semibold hover:text-primary transition-colors flex items-center gap-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="material-symbols-outlined text-sm text-primary">help</span>
                    FAQ
                  </Link>
                </div>
              </div>
              
              <Link
                to="/fioristi"
                className="text-sm font-semibold hover:text-primary transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Fiori e Pensieri
              </Link>
              <Link
                to="/chi-siamo"
                className="text-sm font-semibold hover:text-primary transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Chi Siamo
              </Link>
              <Link
                to="/contatti"
                className="text-sm font-semibold hover:text-primary transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('header.contact')}
              </Link>
              
              {/* Donazioni Button - Mobile */}
              <Link
                to="/associazioni"
                className="flex items-center justify-center gap-2 px-4 py-3 bg-primary/10 text-primary font-semibold rounded-lg hover:bg-primary hover:text-white transition-all"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="material-symbols-outlined text-sm">favorite</span>
                Donazioni
              </Link>
              {isAuthenticated ? (
                <>
                  {/* Mobile Dashboard Button with Badge */}
                  <button
                    onClick={() => {
                      handleAreaRiservata();
                      setMobileMenuOpen(false);
                    }}
                    className="relative flex items-center gap-2 px-4 py-2 text-sm font-bold border-2 border-primary text-charcoal rounded-lg hover:bg-primary transition-all w-full justify-center"
                  >
                    <span className="material-symbols-outlined text-sm">
                      {isAdmin ? 'admin_panel_settings' : isAgenzia ? 'business' : isBlogger ? 'edit_note' : isFiorista ? 'local_florist' : isProfessionista ? 'work' : 'person'}
                    </span>
                    {isAdmin ? 'Pannello Admin' : (isAgenzia || isFiorista || isProfessionista || isBlogger) ? 'Dashboard' : 'Area Personale'}
                    
                    {/* Mobile Notification Badge */}
                    {isAgenzia && unreadFeedbackCount > 0 && (
                      <span className="ml-2 min-w-[20px] h-5 px-1.5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full">
                        {unreadFeedbackCount > 99 ? '99+' : unreadFeedbackCount}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-semibold border-2 border-soft-cream text-charcoal rounded-lg hover:bg-soft-cream transition-all w-full justify-center"
                  >
                    <span className="material-symbols-outlined text-sm">logout</span>
                    Esci
                  </button>
                </>
              ) : (
                <button
                  onClick={() => {
                    navigate('/login');
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-bold border-2 border-primary text-charcoal rounded-lg hover:bg-primary transition-all w-full justify-center"
                >
                  <span className="material-symbols-outlined text-sm">lock</span>
                  Area Riservata
                </button>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
